/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Grid, PageGridContainer, Tab, TabContext, TabPanel, Tabs, Typography } from '@coursera/cds-core';

import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import { CollectionsContainer } from 'bundles/collections-common';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import CarouselCollectionType from 'bundles/unified-description-page-common/components/recommendations/CarouselCollectionType';
import { toCarouselCollection } from 'bundles/unified-description-page-common/components/recommendations/toCarouselCollection';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import type { CollectionEntityFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/CollectionEntityFragment';

import _t from 'i18n!nls/xdp';

type CourseCollections = {
  degreesCollection?: CarouselCollection;
  recommendationsCollection?: CollectionEntityFragment[] | null;
  relatedCoursesCollection?: CollectionEntityFragment[] | null;
};

type Props = CourseCollections & {
  domainName?: string;
  anchorId?: string;
  degreesLoading?: boolean;
};

const RECS_TAB_VALUE = '1';

const RELATED_TAB_VALUE = '2';

const DEGREES_TAB_VALUE = '3';

const getInitialTabValue = ({ recommendationsCollection, relatedCoursesCollection }: CourseCollections) => {
  if (recommendationsCollection) {
    return RECS_TAB_VALUE;
  } else if (relatedCoursesCollection) {
    return RELATED_TAB_VALUE;
  } else {
    return DEGREES_TAB_VALUE;
  }
};

const getTabHeaders = ({
  degreesCollection,
  recommendationsCollection,
  relatedCoursesCollection,
}: CourseCollections) => {
  const tabs = [];

  if (recommendationsCollection && recommendationsCollection.length > 0) {
    tabs.push(<Tab label={_t('Recommended')} value={RECS_TAB_VALUE} key={RECS_TAB_VALUE} />);
  }

  if (relatedCoursesCollection && relatedCoursesCollection.length > 0) {
    tabs.push(<Tab label={_t('Related courses')} value={RELATED_TAB_VALUE} key={RELATED_TAB_VALUE} />);
  }

  if (degreesCollection && degreesCollection.entities.length > 0) {
    tabs.push(<Tab label={_t('Degrees')} value={DEGREES_TAB_VALUE} key={DEGREES_TAB_VALUE} />);
  }

  return tabs;
};

const getTabPanels = (
  { degreesCollection, recommendationsCollection, relatedCoursesCollection }: CourseCollections,
  subfolderLocaleCode: string | undefined
) => {
  const tabPanels = [];
  if (recommendationsCollection) {
    tabPanels.push(
      <TabPanel value={RECS_TAB_VALUE} key={RECS_TAB_VALUE}>
        <CollectionsContainer
          collections={[
            toCarouselCollection({
              recommendationsCollection,
              collectionId: CarouselCollectionType.RECOMMENDATIONS,
              subfolderLocaleCode,
            }),
          ]}
        />
      </TabPanel>
    );
  }

  if (relatedCoursesCollection) {
    tabPanels.push(
      <TabPanel value={RELATED_TAB_VALUE} key={RELATED_TAB_VALUE}>
        <CollectionsContainer
          collections={[
            toCarouselCollection({
              recommendationsCollection: relatedCoursesCollection,
              collectionId: CarouselCollectionType.RELATED_COURSE,
              subfolderLocaleCode,
            }),
          ]}
        />
      </TabPanel>
    );
  }

  if (degreesCollection) {
    tabPanels.push(
      <TabPanel value={DEGREES_TAB_VALUE} key={DEGREES_TAB_VALUE}>
        <CollectionsContainer collections={[degreesCollection]} />
      </TabPanel>
    );
  }

  return tabPanels;
};

const styles = {
  title: () => css`
    margin-bottom: var(--cds-spacing-400);
  `,
};

const CourseRecommendationsModuleNotConfigurable = ({
  degreesCollection,
  recommendationsCollection,
  relatedCoursesCollection,
  domainName,
  anchorId,
  degreesLoading,
}: Props) => {
  const [currentTabValue, setCurrentTabValue] = useState(
    getInitialTabValue({ degreesCollection, recommendationsCollection, relatedCoursesCollection })
  );
  const { subfolderLocaleCode } = usePageInfo();

  const tabs = getTabHeaders({ degreesCollection, recommendationsCollection, relatedCoursesCollection });
  const tabPanels = getTabPanels(
    {
      degreesCollection,
      recommendationsCollection,
      relatedCoursesCollection,
    },
    subfolderLocaleCode
  );
  const title = domainName ? (
    <FormattedMessage message={_t("Recommended if you're interested in {domainName}")} domainName={domainName} />
  ) : (
    <FormattedMessage message={_t('You might also like')} />
  );
  if (
    !recommendationsCollection?.length &&
    !relatedCoursesCollection?.length &&
    (!degreesCollection || degreesLoading)
  ) {
    return null;
  }

  return (
    <TrackedDiv
      trackingName="recommendations_module"
      trackClicks={false}
      requireFullyVisible={false}
      withVisibilityTracking={true}
    >
      <Section anchorId={anchorId}>
        <PageGridContainer>
          <Grid item xs={12}>
            <Typography variant="h1semibold" component="h3" css={styles.title}>
              {title}
            </Typography>
            <TabContext value={currentTabValue}>
              <Tabs
                aria-label={_t('Recommendation Tabs')}
                variant="section"
                onChange={(newValue) => {
                  setCurrentTabValue(newValue);
                }}
              >
                {tabs}
              </Tabs>
              {tabPanels}
            </TabContext>
          </Grid>
        </PageGridContainer>
      </Section>
    </TrackedDiv>
  );
};

export default CourseRecommendationsModuleNotConfigurable;
